import {
  Link as RouterLink,
  useNavigate as useRouterNavigate,
  useParams as useRouterParams,
  useRouterState,
  useSearch as useRouterSearch,
} from '@tanstack/react-router';
import type { AnchorHTMLAttributes } from 'react';
import { useBoardBasePath } from './board-runtime';

/**
 * A package path at the host's mount. Every page — Meetings included — lives beneath the mount, so
 * a meeting's own pages sit under the Meetings page's address (Jonathan, 2026-09-28: its row stays
 * lit there); the mount itself is the host's Board home, which replaces itself with Meetings.
 */
export function boardPath(basePath: string, path: string): string {
  if (!path.startsWith('/') || basePath.length === 0) return path;
  return `${basePath}${path}`;
}

interface BoardDestination {
  to: string;
  params?: Record<string, string>;
  /**
   * The destination's search, e.g. the Governance page's `?tab=`. A number is written bare
   * (`?year_end_month=6`, the Form 990 page's month and day); the router quotes a numeric string.
   */
  search?: Record<string, string | number>;
  replace?: boolean;
}

function addressedPath(to: string, params: Record<string, string> = {}): string {
  return to.replace(/\$([A-Za-z][A-Za-z0-9]*)/g, (_token, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`Missing Board route parameter: ${name}`);
    return encodeURIComponent(value);
  });
}

export function Link({
  to,
  params,
  search,
  exact,
  ...props
}: Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> &
  BoardDestination & {
    /**
     * Current only at exactly this address. Otherwise the router marks the link current
     * (`aria-current="page"`, overriding any value passed here) at every address beneath its own
     * too: a meeting's Overview tab claimed the Packet, Meeting record and Minutes pages as well.
     */
    exact?: boolean;
    /**
     * `false` leaves the window's scroll to the destination. Otherwise the router scrolls to the top
     * once the new location has rendered (router-core `_scroll.next = resetScroll ?? true`), after a
     * page that brought its own part into view on arrival has done so (the Form 990 page's `?line=`).
     */
    resetScroll?: boolean;
  }) {
  const basePath = useBoardBasePath();
  return (
    <RouterLink
      {...props}
      to={boardPath(basePath, addressedPath(to, params))}
      {...(search === undefined ? {} : { search })}
      {...(exact ? { activeOptions: { exact: true } } : {})}
    />
  );
}

export function useNavigate() {
  const navigate = useRouterNavigate();
  const basePath = useBoardBasePath();
  return ({ to, params, search, ...options }: BoardDestination) =>
    navigate({
      ...options,
      to: boardPath(basePath, addressedPath(to, params)),
      ...(search === undefined ? {} : { search }),
    });
}

/** The current address's search, read tolerantly: the matched host route owns any validation. */
export function useSearch(): Record<string, unknown> {
  return useRouterSearch({ strict: false }) as Record<string, unknown>;
}

/**
 * The path of the page on screen, without its search: the deepest rendered match's. During a
 * navigation the router's location already names the next page while this one is still drawn.
 */
export function usePathname(): string {
  return useRouterState({
    select: (state) => state.matches[state.matches.length - 1]?.pathname ?? state.location.pathname,
  });
}

/** Params belong to the matched host route, never a package-hard-coded route ID. */
type ParamNames<Path extends string> = Path extends `${string}$${infer Key}/${infer Rest}`
  ? Key | ParamNames<Rest>
  : Path extends `${string}$${infer Key}`
    ? Key
    : never;

export function useParams<const Path extends string>(options: { from: Path }) {
  // The legacy pattern supplies type-level parameter names, not a host route ID.
  void options;
  return useRouterParams({ strict: false }) as Record<ParamNames<Path>, string>;
}
